// Import your types
import { Contact } from '../types';
import { createAxiosWithSubscriptionInterceptor } from '../utils/subscriptionInterceptor';

// Define the API base URL
const API_URL = '/api';
const axiosInstance = createAxiosWithSubscriptionInterceptor();

export const api = {
  // Auth
  getCurrentUser: async () => {
    try {
      const response = await axiosInstance.get('/me');
      return response.data;
    } catch (error) {
      throw new Error('Not authenticated');
    }
  },

  loginWithGoogle: () => {
    window.location.href = `${API_URL}/auth/google`;
  },

  logout: async () => {
    try {
      // Call the logout endpoint
      const response = await axiosInstance.post('/auth/logout');
      
      if (response.data.success) {
        // Clear any client-side state
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear axios defaults
        delete axiosInstance.defaults.headers.common['Authorization'];
        
        // Force a hard reload to ensure all state is cleared
        window.location.href = '/login';
        window.location.reload();
      } else {
        throw new Error(response.data.message || 'Logout failed');
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if the logout fails, reset the client-side state
      localStorage.clear();
      sessionStorage.clear();
      delete axiosInstance.defaults.headers.common['Authorization'];
      window.location.href = '/login';
      window.location.reload();
    }
  },
  
  // Token-based authentication
  verifyToken: async (token: string) => {
    try {
      const response = await axiosInstance.post('/verify-token', { token });
      return response.data;
    } catch (error) {
      console.error('Token verification failed:', error);
      throw error;
    }
  },

  // Contacts
  getContacts: async () => {
    try {
      const response = await axiosInstance.get('/contacts');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      throw error;
    }
  },

  getContact: async (id: number) => {
    try {
      const response = await axiosInstance.get(`/contacts/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch contact ${id}:`, error);
      throw error;
    }
  },

  createContact: async (contactData: Omit<Contact, 'id'>) => {
    try {
      const response = await axiosInstance.post('/contacts', contactData);
      return response.data;
    } catch (error) {
      console.error('Failed to create contact:', error);
      throw error;
    }
  },

  updateContact: async (id: number, contactData: Partial<Contact>) => {
    try {
      const response = await axiosInstance.put(`/contacts/${id}`, contactData);
      return response.data;
    } catch (error) {
      console.error('Failed to update contact:', error);
      throw error;
    }
  },

  deleteContact: async (id: number) => {
    try {
      console.log('Deleting contact with ID:', id);
      
      const response = await axiosInstance.delete(`/contacts/${id}`);
      return true;
    } catch (error) {
      console.error('API deleteContact error:', error);
      throw error;
    }
  },

  // Groups
  getGroups: async () => {
    try {
      const response = await axiosInstance.get('/groups');
      return response.data;
    } catch (error) {
      console.error('getGroups error:', error);
      throw error;
    }
  },

  createGroup: async (groupData: any) => {
    try {
      const response = await axiosInstance.post('/groups', groupData);
      return response.data;
    } catch (error) {
      console.error('Failed to create group:', error);
      throw error;
    }
  },

  updateGroup: async (id: number, groupData: any) => {
    try {
      const response = await axiosInstance.put(`/groups/${id}`, groupData);
      return response.data;
    } catch (error) {
      console.error('Failed to update group:', error);
      throw error;
    }
  },

  deleteGroup: async (id: number) => {
    try {
      await axiosInstance.delete(`/groups/${id}`);
    } catch (error) {
      console.error('Failed to delete group:', error);
      throw error;
    }
  },

  // Todos
  getTodos: async (contactId: number) => {
    try {
      const response = await axiosInstance.get(`/todos/contact/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch todos:', error);
      throw error;
    }
  },

  createTodo: async (contactId: number, todoData: any) => {
    try {
      const response = await axiosInstance.post(`/todos/contact/${contactId}`, todoData);
      return response.data;
    } catch (error) {
      console.error('Failed to create todo:', error);
      throw error;
    }
  },

  updateTodo: async (todoId: string, todoData: any) => {
    try {
      const response = await axiosInstance.put(`/todos/${todoId}`, todoData);
      return response.data;
    } catch (error) {
      console.error('Failed to update todo:', error);
      throw error;
    }
  },

  deleteTodo: async (todoId: string) => {
    try {
      await axiosInstance.delete(`/todos/${todoId}`);
    } catch (error) {
      console.error('Failed to delete todo:', error);
      throw error;
    }
  },

  // Interactions
  getInteractions: async (contactId: number) => {
    try {
      const response = await axiosInstance.get(`/interactions/contact/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch interactions:', error);
      throw error;
    }
  },

  createInteraction: async (contactId: number, interactionData: any) => {
    try {
      const response = await axiosInstance.post(`/interactions/contact/${contactId}`, interactionData);
      return response.data;
    } catch (error) {
      console.error('Failed to create interaction:', error);
      throw error;
    }
  },

  updateInteraction: async (interactionId: number, interactionData: any) => {
    try {
      const response = await axiosInstance.put(`/interactions/${interactionId}`, interactionData);
      return response.data;
    } catch (error) {
      console.error('Failed to update interaction:', error);
      throw error;
    }
  },

  deleteInteraction: async (interactionId: number) => {
    try {
      console.log('Deleting interaction with ID:', interactionId);
      
      await axiosInstance.delete(`/interactions/${interactionId}`);
      return true;
    } catch (error) {
      console.error('API deleteInteraction error:', error);
      throw error;
    }
  },

  // User Settings
  getUserSettings: async () => {
    try {
      const response = await axiosInstance.get('/user/settings');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
      throw error;
    }
  },

  updateUserSettings: async (settings: any) => {
    try {
      const response = await axiosInstance.put('/user/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Failed to update user settings:', error);
      throw error;
    }
  },

  // This function checks all contacts and updates their warm/cold status based on settings
  updateContactStatuses: async () => {
    try {
      const response = await axiosInstance.post('/contacts/update-status');
      return response.data;
    } catch (error) {
      console.error('Failed to update contact statuses:', error);
      throw error;
    }
  },
  
  // Get reminder counts for multiple contacts at once
  getBatchReminderCounts: async (contactIds: number[]) => {
    if (!contactIds || contactIds.length === 0) return {};
    
    try {
      const response = await axiosInstance.post('/reminders/counts', { contactIds });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch batch reminder counts:', error);
      return {};
    }
  },
  
  // Notifications
  getNotifications: async () => {
    try {
      const response = await axiosInstance.get('/notifications');
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },
  
  markNotificationAsRead: async (id: string) => {
    try {
      const response = await axiosInstance.patch(`/notifications/${id}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },
  
  markAllNotificationsAsRead: async () => {
    try {
      const response = await axiosInstance.patch('/notifications/mark-all-read');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },
  
  deleteNotification: async (id: string) => {
    try {
      await axiosInstance.delete(`/notifications/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },
  
  generateNotifications: async () => {
    try {
      const response = await axiosInstance.post('/notifications/generate/all');
      return response.data;
    } catch (error) {
      console.error('Error generating notifications:', error);
      throw error;
    }
  },

  telegramConnection: {
    getStatus: async () => {
      try {
        const response = await axiosInstance.get('/telegram/status');
        return response.data;
      } catch (error) {
        console.error('Error fetching Telegram status:', error);
        throw error;
      }
    },
    
    generatePairingCode: async () => {
      try {
        const response = await axiosInstance.post('/telegram/pairing-code');
        return response.data;
      } catch (error) {
        console.error('Error generating Telegram pairing code:', error);
        throw error;
      }
    },
    
    disconnect: async () => {
      try {
        const response = await axiosInstance.post('/telegram/disconnect');
        return response.data;
      } catch (error) {
        console.error('Error disconnecting Telegram:', error);
        throw error;
      }
    }
  }
};